import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Table, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Message from "../components/Message";
import { FaTimes } from "react-icons/fa";
import { useProfileMutation } from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import { useGetUserOrdersQuery } from "../slices/ordersApiSlice";
import { useSendOTPEmailMutation, useVerifyOTPUpdateEmailMutation } from "../slices/emailApiSlice";
import { dateFormatterIST } from "../utils/dateHelper";

const ProfileScreen = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isOTPSent, setIsOTPSent] = useState(false);

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  const [updateProfile, { isLoading: isLoadingUpdateProfile }] = useProfileMutation();
  const [sendOTPEmail, { isLoading: isSendingOTP }] = useSendOTPEmailMutation();
  const [verifyOTPUpdateEmail, { isLoading: isVerifyingOTP }] = useVerifyOTPUpdateEmailMutation();

  const { data: orders, isLoading: isLoadingGetUserOrders, error } = useGetUserOrdersQuery();

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name);
      setEmail(userInfo.email);
    }
  }, [userInfo]);

  // Update Name
  const updateNameHandler = async (e) => {
    e.preventDefault();
    try {
      const res = await updateProfile({ _id: userInfo._id, name }).unwrap();
      dispatch(setCredentials(res));
      toast.success("Name updated successfully", { autoClose: 1000 });
    } catch (error) {
      toast.error(error?.data?.message || error?.error);
    }
  };

  // Send OTP
  const sendOTPHandler = async () => {
    try {
      await sendOTPEmail({ email }).unwrap();
      setIsOTPSent(true);
      toast.success("OTP sent to email", { autoClose: 1000 });
    } catch (error) {
      toast.error(error?.data?.message || error?.error);
    }
  };

  // Verify OTP + update email
  const verifyOTPHandler = async (e) => {
    e.preventDefault();
    try {
      const updatedUser = await verifyOTPUpdateEmail({ email, otp }).unwrap();
      dispatch(setCredentials(updatedUser)); // refresh Redux with updated user
      toast.success("Email updated successfully", { autoClose: 1000 });
      setIsOTPSent(false);
      setOtp("");
    } catch (error) {
      toast.error(error?.data?.message || error?.error);
    }
  };

  return (
    <Row>
      <Col md={3}>
        <h2>User Profile</h2>

        {/* Update Name */}
        <Form onSubmit={updateNameHandler}>
          <Form.Group controlId="name" className="my-2">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
          <Button type="submit" variant="primary" className="my-2" disabled={isLoadingUpdateProfile}>
            {isLoadingUpdateProfile ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                {" Updating..."}
              </>
            ) : (
              "Update Name"
            )}
          </Button>
        </Form>

        {/* Update Email */}
        <Form onSubmit={isOTPSent ? verifyOTPHandler : (e) => { e.preventDefault(); sendOTPHandler(); }}>
          <Form.Group controlId="email" className="my-2">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Group>

          {isOTPSent && (
            <Form.Group controlId="otp" className="my-2">
              <Form.Label>Enter OTP</Form.Label>
              <Form.Control
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </Form.Group>
          )}

          <Button
            type="submit"
            variant="primary"
            className="my-2"
            disabled={isSendingOTP || isVerifyingOTP}
          >
            {(isSendingOTP || isVerifyingOTP) ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                {isOTPSent ? " Verifying..." : " Sending..."}
              </>
            ) : (
              isOTPSent ? "Verify OTP & Update Email" : "Update Email"
            )}
          </Button>
        </Form>
      </Col>

      {/* Orders Table */}
      <Col md={9}>
        <h2>User Orders</h2>
        {isLoadingGetUserOrders ? (
          <Spinner animation="border" role="status" />
        ) : error ? (
          <Message variant="danger">{error?.data?.message || error.error}</Message>
        ) : (
          <Table striped hover responsive className="table-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>DATE</th>
                <th>TOTAL</th>
                <th>PAID</th>
                <th>DELIVERED</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders
                ?.slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((order) => (
                  <tr key={order._id}>
                    <td>
                      <Link to={`/order/${order._id}`}>{order._id}</Link>
                    </td>
                    <td>{dateFormatterIST(order.createdAt)}</td>
                    <td>₹{order.totalPrice}</td>
                    <td>
                      {order.isPaid ? (
                        `Paid on ${order.paidAt.substring(0, 10)}`
                      ) : (
                        <FaTimes style={{ color: "red" }} />
                      )}
                    </td>
                    <td>
                      {order.delivery.isDelivered ? (
                        `Delivered on ${order.delivery.deliveredAt.substring(0, 10)}`
                      ) : (
                        <FaTimes style={{ color: "red" }} />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        )}
      </Col>
    </Row>
  );
};

export default ProfileScreen;